import { useCallback, useState } from "react";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

export function UploadCard({
  ownerType,
  ownerId,
  onUploaded,
}: {
  ownerType: string;
  ownerId: string;
  onUploaded(): Promise<void>;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = useCallback(async () => {
    if (!file) {
      setUploadError(t("documents.uploadCard.chooseFirst"));
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      await api.uploadAttachment({ ownerType, ownerId, file });
      push(t("documents.uploadCard.uploadedToast"), file.name);
      setFile(null);
      const input = document.getElementById("document-file") as HTMLInputElement | null;
      if (input) input.value = "";
      await onUploaded();
    } catch (err) {
      if (err instanceof ApiClientError) setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  }, [file, onUploaded, ownerId, ownerType, push, t]);

  return (
    <Card className="mb-5">
      <h2 className="mb-3 text-sm font-semibold text-ink">
        {t("documents.uploadCard.title")}
      </h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="document-file" className="text-sm font-medium text-ink">
            {t("documents.uploadCard.fileLabel")}
          </label>
          <input
            id="document-file"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setUploadError(null);
            }}
            className="h-11 rounded-[var(--radius-card)] border border-line bg-surface-raised px-2 py-2 text-sm file:me-2 file:border-0 file:bg-surface-inset file:rounded file:px-3 file:py-1.5"
          />
        </div>
        <Button variant="primary" onClick={handleUpload} disabled={uploading}>
          {uploading
            ? t("documents.uploadCard.uploading")
            : t("documents.uploadCard.upload")}
        </Button>
      </div>
      {uploadError ? (
        <p role="alert" className="mt-2 text-sm font-medium text-action-danger">
          {uploadError}
        </p>
      ) : null}
    </Card>
  );
}
